import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { UserTaskNames } from 'app/@types/dto/user-task-names.model';
import { userRoles } from 'app/@types/dto/userRoles.model';

/**
 * Official uiFeature names
 * which are actually the same as UserTasks
 */
export const UiFeatureNames = UserTaskNames;

/**
 * Well-known UIFeatures which are for Admin purposes
 */

/**
 * Class wrapping a single UiFeature,
 *  - possibly constrained to an organizationId
 *  - possibly further constrained to an organizationEntityId
 *  - with qualifiers that can be tested for specific behaviour
 */
export class UiFeature {
    name: string;
    organizationId?: string;
    organizationEntityId?: string;
    qualifiers: string[];
    readOnly: boolean;
    constructor(
        name: string,
        organizationId: string | undefined,
        organizationEntityId: string | undefined,
        qualifiers: string[],
    ) {
        this.name = name;
        this.organizationId = organizationId;
        this.organizationEntityId = organizationEntityId;
        this.qualifiers = qualifiers;
        this.readOnly = qualifiers.includes('ro');
    }
}

// Re-export ServiceLevels as type enum
export type UserRoleEnum = keyof typeof userRoles;

export type CheckAccessResult = 'none' | 'ro' | 'rw';

/**
 * Full Feature object
 */
export class Feature {
    private uiFeatures: UiFeature[] = [];
    private organizationEntities: OrganizationEntityDto[] | undefined = undefined;
    private userRole?: UserRoleEnum;
    private canSudo: boolean;
    private isSudo: boolean;

    /* Currently selected organization */
    public organization: OrganizationDto | undefined = undefined;

    constructor(
        uiFeatures: UiFeature[],
        canSudo: boolean,
        isSudo: boolean,
        organization?: OrganizationDto,
        organizationEntities?: OrganizationEntityDto[],
        userRole?: UserRoleEnum,
    ) {
        this.uiFeatures = uiFeatures;
        this.canSudo = canSudo;
        this.isSudo = isSudo;
        this.organization = organization;
        this.organizationEntities = organizationEntities;
        this.userRole = userRole;
    }

    /**
     * Check for at least one Admin uiFeature within current uiFeatures
     *    does not check if it depends or not of an organization
     * @returns true/false
     */

    /**
     * Check for at least one Admin uiFeature within current uiFeatures
     *    does not check if it depends or not of an organization
     * @returns true/false
     */
    public hasSudoFeature(): boolean {
        return this.canSudo && this.isSudo;
    }

    /**
     * Check for a uiFeature,
     *    possibly for an organization,
     *    further possibly for an organization entity
     * This method returns True when we have at least one matching instance (for activating a Tab for example)
     * @param uiFeatureName
     * @param organizationId
     * @param organizationEntityId
     * @returns true/false
     */
    public hasUiFeature(
        uiFeatureName: string,
        organizationId: string | undefined,
        organizationEntityId: string | undefined,
    ): boolean {
        return this.uiFeatures.some(
            (x) =>
                x.name === uiFeatureName &&
                (x.organizationId === undefined || x.organizationId === organizationId) &&
                (x.organizationEntityId === undefined ||
                    organizationEntityId === undefined ||
                    x.organizationEntityId === organizationEntityId),
        );
    }

    /**
     * Get a uiFeature by name,
     *    possibly for an organization,
     *    further possibly for an organization entity
     * @param uiFeatureName
     * @param organizationId
     * @param organizationEntityId
     * @returns the first UiFeature meeting the request, else undefined
     */
    public getUiFeature(
        uiFeatureName: string,
        organizationId: string | undefined,
        organizationEntityId: string | undefined,
    ): UiFeature | undefined {
        const features = this.uiFeatures.filter(
            (x) =>
                x.name === uiFeatureName &&
                (x.organizationId === undefined || x.organizationId === organizationId) &&
                (x.organizationEntityId === undefined ||
                    organizationEntityId === undefined ||
                    x.organizationEntityId === organizationEntityId),
        );
        return features.length > 0 ? features[0] : undefined;
    }

    /**
     * Check access level for a uiFeature by name,
     *    possibly for an organization,
     *    further possibly for an organization entity
     * This method returns the access level for a specific UiFeature
     * @param uiFeatureName
     * @param organizationId
     * @param organizationEntityId : specific-id, undefined=require-global, null=ignore
     * @returns 'none' | 'ro' | 'rw'
     */
    public checkAccess(
        uiFeatureName: string,
        organizationId: string | undefined,
        organizationEntityId: string | undefined | null,
    ): CheckAccessResult {
        const features = this.uiFeatures.filter(
            (x) =>
                x.name === uiFeatureName &&
                (x.organizationId === undefined || x.organizationId === organizationId) &&
                (x.organizationEntityId === undefined ||
                    organizationEntityId === null ||
                    x.organizationEntityId === organizationEntityId),
        ); // organizationEntityId === null to ignore entity
        if (features.length === 0) {
            return 'none';
        }
        return features.some((x) => !x.readOnly) ? 'rw' : 'ro';
    }

    public getUserRole(): UserRoleEnum | undefined {
        return this.userRole;
    }

    /**
     * Get possible organization entities for a uiFeature.
     * This method partially replicates the backend user authorization code,
     * see AuthorizationManager.HasPermissionAsync()
     * @param uiFeatureName
     * @returns List of possible OrganizationEntityDto
     */
    public getOrganizationEntities(uiFeatureName: string): OrganizationEntityDto[] {
        if (!this.organizationEntities) {
            return [];
        }

        // Get the list of organizationEntityIds from uiFeatures filtered by the given uiFeature
        const possibleOrganizationEntityIds = this.uiFeatures
            .filter(
                (x) =>
                    x.name === uiFeatureName &&
                    (x.organizationId === undefined || x.organizationId === this.organization?.uid),
            )
            .map((x) => x.organizationEntityId);

        // When at least one uiFeature entry has no restriction on organizationEntityId, then all organizationEntities are allowed
        if (possibleOrganizationEntityIds.some((x) => x === undefined)) {
            return this.organizationEntities;
        }

        // Return the list of entities filtered by possible ones
        return this.organizationEntities.filter((x) => possibleOrganizationEntityIds.includes(x.id));
    }
}

export const DefaultFeature = new Feature([], false, false);
